import type { MouseEvent } from 'react';
import { useGT } from 'gt-react';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { toast } from 'src/components/ui/toast';
import { cn } from '@/lib/utils';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { getAssignmentLinePartNumber } from '../utils/linePartNumber';

export function ProductCell({ line }: { line: AssignmentLine }) {
  const gt = useGT();
  const partNumber = getAssignmentLinePartNumber(line);
  const { copy } = useCopyToClipboard();
  const copyHint = gt('Double-click to copy part number', {
    context: 'buyer board item column copy hint',
  });

  const handleDoubleClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (!partNumber) return;
    void copy(partNumber).then((ok) => {
      if (!ok) return;
      toast.success(
        gt('Part number copied', {
          context: 'buyer board item column copy toast',
        }),
      );
    });
  };

  return (
    <span
      className={cn(
        'flex min-w-0 items-center gap-1',
        partNumber && 'cursor-copy',
      )}
      onDoubleClick={partNumber ? handleDoubleClick : undefined}
      title={partNumber ? copyHint : undefined}
    >
      <span className="truncate text-gray-600">
        {line.productSku ?? line.productName}
      </span>
    </span>
  );
}

/** The item description; blank when none is on file, never the SKU. */
export function ProductDescriptionCell({ line }: { line: AssignmentLine }) {
  if (!line.productDescription) return null;
  return (
    <span
      className="block truncate text-gray-600"
      title={line.productDescription}
    >
      {line.productDescription}
    </span>
  );
}
